import React, { useMemo } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const HANDLE_RANGE = 8;
const HANDLE_STEP = 0.0625;

export default function CurveHandlePanel({
  open,
  selectedCurveId,
  curveSegments = [],
  curveHandles = [],
  measurements = {},
  canEdit = true,
  onClose,
  onHandleValueChange,
  onHandleBatchChange,
}) {
  const selectedCurve = useMemo(
    () => curveSegments.find((segment) => segment.id === selectedCurveId),
    [curveSegments, selectedCurveId],
  );

  const selectedHandles = useMemo(
    () => curveHandles.filter((handle) => handle.curveId === selectedCurveId),
    [curveHandles, selectedCurveId],
  );

  if (!open || !selectedCurve) return null;

  const applyAxis = (key, value) => {
    onHandleValueChange?.(key, Number(value));
  };

  const resetCurrentCurve = () => {
    const payload = selectedHandles.reduce((acc, handle) => {
      acc[handle.xKey] = 0;
      acc[handle.yKey] = 0;
      return acc;
    }, {});

    onHandleBatchChange?.(payload);
  };

  return (
    <div className="fixed right-4 top-20 z-50 w-[360px] rounded-xl border bg-background/95 shadow-2xl backdrop-blur" data-testid="curve-handle-panel">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <p className="text-sm font-semibold" data-testid="curve-handle-panel-title">Curve Controls</p>
          <p className="text-xs text-muted-foreground" data-testid="curve-handle-panel-subtitle">{selectedCurve.label}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={resetCurrentCurve}
            disabled={!canEdit || selectedHandles.length === 0}
            data-testid="curve-handle-panel-reset-current-button"
          >
            <RotateCcw size={14} className="mr-1" /> Reset Curve
          </Button>
          <Button type="button" size="icon" variant="ghost" onClick={onClose} data-testid="curve-handle-panel-close-button">
            <X size={16} />
          </Button>
        </div>
      </div>

      <div className="max-h-[68vh] overflow-y-auto p-4 space-y-4">
        {selectedHandles.map((handle) => {
          const xValue = Number(measurements?.[handle.xKey]) || 0;
          const yValue = Number(measurements?.[handle.yKey]) || 0;
          const lockAxis = handle.lockAxis || null;
          const xLocked = lockAxis === 'y';
          const yLocked = lockAxis === 'x';

          return (
            <div key={handle.id} className="rounded-lg border p-3 space-y-3" data-testid={`curve-handle-panel-handle-${handle.id}`}>
              <p className="text-xs font-semibold text-muted-foreground">{handle.label}</p>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span>X Offset</span>
                  <span data-testid={`curve-handle-panel-${handle.id}-x-value`}>{xValue.toFixed(4)}</span>
                </div>
                <Slider
                  min={-HANDLE_RANGE}
                  max={HANDLE_RANGE}
                  step={HANDLE_STEP}
                  value={[xValue]}
                  onValueChange={(values) => applyAxis(handle.xKey, values[0])}
                  disabled={!canEdit || xLocked}
                  data-testid={`curve-handle-panel-${handle.id}-x-slider`}
                />
                <Input
                  type="number"
                  step={HANDLE_STEP}
                  value={xValue}
                  onChange={(event) => applyAxis(handle.xKey, event.target.value)}
                  disabled={!canEdit || xLocked}
                  data-testid={`curve-handle-panel-${handle.id}-x-input`}
                />
                <p className="text-[10px] text-muted-foreground" data-testid={`curve-handle-panel-${handle.id}-x-step-note`}>
                  {xLocked ? 'Locked (vertical-handle rule)' : 'Step: 0.0625 (1/16")'}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span>Y Offset</span>
                  <span data-testid={`curve-handle-panel-${handle.id}-y-value`}>{yValue.toFixed(4)}</span>
                </div>
                <Slider
                  min={-HANDLE_RANGE}
                  max={HANDLE_RANGE}
                  step={HANDLE_STEP}
                  value={[yValue]}
                  onValueChange={(values) => applyAxis(handle.yKey, values[0])}
                  disabled={!canEdit || yLocked}
                  data-testid={`curve-handle-panel-${handle.id}-y-slider`}
                />
                <Input
                  type="number"
                  step={HANDLE_STEP}
                  value={yValue}
                  onChange={(event) => applyAxis(handle.yKey, event.target.value)}
                  disabled={!canEdit || yLocked}
                  data-testid={`curve-handle-panel-${handle.id}-y-input`}
                />
                <p className="text-[10px] text-muted-foreground" data-testid={`curve-handle-panel-${handle.id}-y-step-note`}>
                  {yLocked ? 'Locked (horizontal-handle rule)' : 'Step: 0.0625 (1/16")'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
